"use client";

import Link from "next/link";
import { Authenticator, ThemeProvider } from "@aws-amplify/ui-react";
import { Button } from "@/components/ui/button";
import { isCognitoConfigured } from "@/lib/amplify-config";
import { thexisTheme } from "@/lib/amplify-theme";
import { authServices } from "@/lib/amplify-auth-services";
import { AuthCard } from "@/components/auth/AuthCard";

// LEG-104a P6 (user-stated guardrail): sign-up is gated by an env var.
// Default behavior in production: invite-only message.
//
// Four-layer defence:
//   1. Cognito User Pool: self-service sign-up disabled in AWS Console
//      (manual step — see CLAUDE.md). Even if someone hits the Cognito
//      Hosted UI directly, AWS rejects.
//   2. THIS env gate: NEXT_PUBLIC_ALLOW_SIGNUP must be "true" for the
//      sign-up form to render. Default off.
//   3. Backend auto-provision block (auth.ts): a Cognito user without
//      an app-side users row hits ONBOARDING_REQUIRED, gets routed
//      through the wizard.
//   4. Phase 7 invite flow: only signed JWT invite tokens (jose, 72h,
//      single-use) can create a user inside an existing firm.
//
// The user runs locally with NEXT_PUBLIC_ALLOW_SIGNUP=true to create
// test accounts at will. Production deploys keep the env var off.
const ALLOW_SIGNUP = process.env.NEXT_PUBLIC_ALLOW_SIGNUP === "true";

function InviteOnlyMessage() {
  return (
    <AuthCard
      eyebrow="Invite only"
      heading="Sign-up by invite."
      lede="Thexis onboards firms on referral. Ask your firm admin for an invite link, or request access from the team."
    >
      <Link href="/login" className="w-full">
        <Button variant="outline" className="w-full">
          Sign in
        </Button>
      </Link>
    </AuthCard>
  );
}

function CognitoSignupForm() {
  // Reuses the Authenticator widget but starts on the signUp tab.
  // Cognito accepts the sign-up only if the User Pool's "Self-service
  // sign-up" is enabled — kept OFF in production.
  return (
    <AuthCard
      eyebrow="Dev mode — sign-up enabled"
      heading="Create your account."
      lede="Local dev only. Production sign-up stays invite-gated."
    >
      <ThemeProvider theme={thexisTheme}>
        <Authenticator initialState="signUp" services={authServices}>
          {() => <></>}
        </Authenticator>
      </ThemeProvider>
    </AuthCard>
  );
}

function DevModeMockNotice() {
  // No Cognito configured AND no signup-allowed flag — dev mock mode
  // doesn't need a sign-up flow because test tokens are pre-issued.
  return (
    <AuthCard
      eyebrow="Dev mode"
      heading="No sign-up needed."
      lede="Mock auth uses pre-issued test tokens. Use the dev login to enter as Analyst or Counsel."
    >
      <Link href="/login" className="w-full">
        <Button variant="outline" className="w-full">
          Dev login
        </Button>
      </Link>
    </AuthCard>
  );
}

export default function SignupPage() {
  // F6 (plan-eng-review 2026-04-30): only emit env-state data-attrs in
  // test mode. Production DOM no longer leaks ALLOW_SIGNUP /
  // isCognitoConfigured. The values are still discoverable from the
  // bundle, but removing them from the DOM tightens the surface for
  // page-scraping enumeration of deployment shape.
  const testAttrs =
    process.env.NODE_ENV === "test"
      ? {
          "data-testid": "signup-page",
          "data-allow-signup": String(ALLOW_SIGNUP),
          "data-cognito-configured": String(isCognitoConfigured),
        }
      : {};

  return (
    <div className="flex flex-1 flex-col" {...testAttrs}>
      {ALLOW_SIGNUP && isCognitoConfigured ? (
        <CognitoSignupForm />
      ) : !isCognitoConfigured ? (
        <DevModeMockNotice />
      ) : (
        <InviteOnlyMessage />
      )}
    </div>
  );
}
